import { PageController } from '@client/components/_core/layout/PageController';
import { StringListSetting } from '@client/components/_core/settings/StringListSetting';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { CLAIM_STATUS_TYPE_LABELS } from '@shared/constants/business/claim-status';
import { claimDecisionsSetting, claimTriageOutcomeSetting } from '@shared/schemas/org-settings';
import { toYesNo } from '@shared/utils';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Filter, Gavel } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/general')({
    staticData: {
        metadata: {
            title: 'General',
        },
        ui: {
            menuDescription: 'Configure claims settings and workflows',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <General />
        </PageController>
    );
}

type QueryParams = RouterInputs['claimStatus']['listClaimStatuses'];

const columnHelper = createColumnHelper<
    RouterOutputs['claimStatus']['listClaimStatuses']['rows'][number],
    QueryParams['orderBy']
>();

function ClaimStatusesList() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.claimStatus.deleteClaimStatus.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({});

    const { data, isPending, error, refetch, isFetching } = trpc.claimStatus.listClaimStatuses.useQuery(input);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No claim statuses yet' },
        pagination: {
            position: 'none',
        },
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell({ row }) {
                    return (
                        <ClaimStatusTag
                            variant="outlined"
                            data={{
                                name: row.name,
                                displayColor: row.displayColor,
                            }}
                        />
                    );
                },
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                sortable: 'type',
                cell({ row }) {
                    return (
                        <Tag variant="outlined" color={row.type === 'OPEN' ? 'green' : 'blue'}>
                            {CLAIM_STATUS_TYPE_LABELS[row.type]}
                        </Tag>
                    );
                },
            }),
            columnHelper.accessor('isDefault', {
                id: 'isDefault',
                header: 'Is default',
                sortable: 'isDefault',
                cell({ row }) {
                    return (
                        <Tag variant="outlined" color={row.isDefault ? 'success' : 'default'}>
                            {toYesNo(row.isDefault)}
                        </Tag>
                    );
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Button.Delete
                            onConfirm={(onSuccess) =>
                                mutate(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            onSuccess();
                                            void trpcUtils.claimStatus.invalidate();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    );
                },
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <PrismaTable
            {...tableProps}
            onRow={({ id }) => ({
                className: 'cursor-pointer',
                onClick(e) {
                    navigate(
                        {
                            to: '/console/$orgId/configuration/claims-products/claims/general/claim-statuses/edit/$claimStatusId',
                            params: {
                                orgId,
                                claimStatusId: id,
                            },
                        },
                        e,
                    );
                },
            })}
        >
            <PrismaTable.Header
                title="Statuses"
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/claims-products/claims/general/claim-statuses/add"
                        params={{
                            orgId,
                        }}
                        variant="tertiary"
                        label="New status"
                    />
                }
            />
        </PrismaTable>
    );
}

function General() {
    return (
        <>
            <Outlet />
            <Space orientation="vertical" size="middle" className="w-full">
                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                    <StringListSetting
                        definition={claimDecisionsSetting}
                        icon={Gavel}
                        title="Claim decisions"
                        noun="decision"
                    />
                    <StringListSetting
                        definition={claimTriageOutcomeSetting}
                        icon={Filter}
                        title="Triage outcomes"
                        noun="outcome"
                    />
                </div>
                <ClaimStatusesList />
            </Space>
        </>
    );
}
